import { useState } from 'react'
import {
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { UserDocument, DocumentStatus } from '../../types'
import {
  useAllUserDocuments,
  useVerifyUserDocument,
  useRejectUserDocument,
  useDownloadUserDocument,
} from '../../hooks/useDocuments'

const statusColors: Record<DocumentStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  UPLOADED: 'bg-blue-100 text-yellow-800',
  VERIFIED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
}

function formatUploaded(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

export default function UserDocumentsSection() {
  const { data: documents = [], isLoading, error, refetch } = useAllUserDocuments()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'all'>('all')

  const verifyMutation = useVerifyUserDocument()
  const rejectMutation = useRejectUserDocument()
  const downloadMutation = useDownloadUserDocument()

  const filtered = documents.filter((d) => {
    const name = d.user ? `${d.user.firstName} ${d.user.lastName}`.toLowerCase() : ''
    const email = d.user?.email?.toLowerCase() ?? ''
    const matchesSearch =
      d.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      name.includes(searchTerm.toLowerCase()) ||
      email.includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const selected = filtered.find((d) => d.id === selectedId) ?? null

  const stats = {
    total: documents.length,
    pending: documents.filter((d) => d.status === DocumentStatus.PENDING || d.status === DocumentStatus.UPLOADED).length,
    verified: documents.filter((d) => d.status === DocumentStatus.VERIFIED).length,
    rejected: documents.filter((d) => d.status === DocumentStatus.REJECTED).length,
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading user documents</h3>
          <button
            onClick={() => refetch()}
            className="mt-2 inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={<DocumentTextIcon className="h-7 w-7 text-gray-600" />} value={stats.total} label="Total" />
        <StatCard icon={<ClockIcon className="h-7 w-7 text-yellow-600" />} value={stats.pending} label="Pending" />
        <StatCard icon={<CheckCircleIcon className="h-7 w-7 text-green-600" />} value={stats.verified} label="Verified" />
        <StatCard icon={<XCircleIcon className="h-7 w-7 text-red-600" />} value={stats.rejected} label="Rejected" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Search by name, email, or file..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as DocumentStatus | 'all')}
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="UPLOADED">Uploaded</option>
              <option value="VERIFIED">Verified</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* List */}
        <div className="space-y-2 max-h-[800px] overflow-y-auto pr-2">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No user documents found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'No documents match your search criteria'
                  : 'No user documents have been uploaded yet'}
              </p>
            </div>
          ) : (
            filtered.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setSelectedId(d.id)}
                className={`w-full text-left bg-white rounded-lg shadow-sm border p-4 transition-colors ${
                  selected?.id === d.id
                    ? 'border-emerald-500 ring-1 ring-emerald-500'
                    : 'border-gray-200 hover:border-emerald-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <h4 className="text-sm font-medium text-gray-900 truncate">{d.originalName}</h4>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      {d.user && (
                        <span className="inline-flex items-center gap-1">
                          <UserIcon className="h-3 w-3" />
                          {d.user.firstName} {d.user.lastName}
                        </span>
                      )}
                      <span>·</span>
                      <span>{(d.fileSize / 1024).toFixed(0)} KB</span>
                      <span>·</span>
                      <span>{formatUploaded(d.uploadedAt)}</span>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[d.status]}`}
                  >
                    {d.status.replace(/_/g, ' ').toLowerCase()}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Review panel */}
        <div className="lg:sticky lg:top-6">
          {selected ? (
            <UserDocReviewPanel
              document={selected}
              onVerify={(notes) =>
                verifyMutation.mutate(
                  { id: selected.id, verificationNotes: notes },
                  { onSuccess: () => setSelectedId(null) },
                )
              }
              onReject={(notes) =>
                rejectMutation.mutate(
                  { id: selected.id, verificationNotes: notes },
                  { onSuccess: () => setSelectedId(null) },
                )
              }
              onDownload={() =>
                downloadMutation.mutate({ id: selected.id, filename: selected.originalName })
              }
              isVerifying={verifyMutation.isPending}
              isRejecting={rejectMutation.isPending}
            />
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No document selected</h3>
              <p className="mt-1 text-sm text-gray-500">Select a document from the list to review</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StatCard({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center">
        {icon}
        <div className="ml-4">
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-gray-600 text-sm">{label}</p>
        </div>
      </div>
    </div>
  )
}

function UserDocReviewPanel({
  document: doc,
  onVerify,
  onReject,
  onDownload,
  isVerifying,
  isRejecting,
}: {
  document: UserDocument
  onVerify: (notes?: string) => void
  onReject: (notes?: string) => void
  onDownload: () => void
  isVerifying: boolean
  isRejecting: boolean
}) {
  const [notes, setNotes] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)
  const isReviewed = doc.status === DocumentStatus.VERIFIED || doc.status === DocumentStatus.REJECTED

  const handleReject = () => {
    if (!notes.trim()) {
      alert('Please provide a rejection reason')
      return
    }
    onReject(notes.trim())
    setNotes('')
    setShowRejectForm(false)
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <DocumentTextIcon className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{doc.originalName}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{doc.type.replace(/_/g, ' ').toLowerCase()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onDownload}
              disabled={isVerifying || isRejecting}
              title="Download"
              className="rounded-md border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* User info */}
        {doc.user && (
          <div className="flex items-start gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <UserIcon className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                {doc.user.firstName} {doc.user.lastName}
              </h4>
              <p className="text-xs text-gray-500">
                {doc.user.email}
                {doc.user.memberId && <span className="ml-2 font-mono">{doc.user.memberId}</span>}
              </p>
            </div>
          </div>
        )}

        {/* Details */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">Uploaded</p>
            <p className="text-gray-900">{formatUploaded(doc.uploadedAt)}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">File size</p>
            <p className="text-gray-900">{(doc.fileSize / 1024).toFixed(0)} KB</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">Type</p>
            <p className="text-gray-900">{doc.mimeType}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">Status</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[doc.status]}`}>
              {doc.status.replace(/_/g, ' ').toLowerCase()}
            </span>
          </div>
        </div>

        {/* Existing notes */}
        {doc.verificationNotes && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-medium text-amber-900">Previous review notes</p>
            <p className="text-xs text-amber-800 mt-0.5">{doc.verificationNotes}</p>
          </div>
        )}

        {/* Actions */}
        {!isReviewed && !showRejectForm && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Optional for verification, required for rejection..."
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowRejectForm(true)}
                disabled={isVerifying || isRejecting}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                <XCircleIcon className="h-4 w-4" />
                Reject
              </button>
              <button
                type="button"
                onClick={() => onVerify(notes.trim() || undefined)}
                disabled={isVerifying || isRejecting}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {isVerifying ? 'Verifying…' : (
                  <>
                    <CheckCircleIcon className="h-4 w-4" />
                    Verify
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Reject confirmation */}
        {showRejectForm && !isReviewed && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm font-medium text-red-900">Confirm rejection</p>
            <p className="text-xs text-red-700 mt-0.5">
              {notes.trim() ? `"${notes.trim()}"` : 'Please enter a reason in the notes field above.'}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => setShowRejectForm(false)}
                disabled={isRejecting}
                className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={isRejecting || !notes.trim()}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isRejecting ? 'Rejecting…' : 'Confirm rejection'}
              </button>
            </div>
          </div>
        )}

        {/* Already reviewed */}
        {isReviewed && (
          <div
            className={`rounded-md border p-3 ${
              doc.status === DocumentStatus.VERIFIED
                ? 'border-green-200 bg-green-50'
                : 'border-red-200 bg-red-50'
            }`}
          >
            <p className={`text-sm font-medium ${doc.status === DocumentStatus.VERIFIED ? 'text-green-900' : 'text-red-900'}`}>
              Document {doc.status.toLowerCase()}
            </p>
            {doc.verifiedAt && (
              <p className={`text-xs mt-0.5 ${doc.status === DocumentStatus.VERIFIED ? 'text-green-700' : 'text-red-700'}`}>
                Reviewed on {formatUploaded(doc.verifiedAt)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
